import { useState } from "react";
import { Icon, Loading, SearchInput, Typography } from "@/components/atoms";
import { AccessLink } from "@/redux/apis/assistants/types";
import { clsx } from "@/utils";
import { ClientAvatar } from "./ClientAvatar";
import { formatRelativeShort } from "./helpers";

export type Selection = { kind: "client" | "pending"; id: string };

interface ClientAccessListProps {
  clients: AccessLink[];
  pending: AccessLink[];
  isLoading: boolean;
  search: string;
  onSearch: (value?: string) => void;
  selected: Selection | null;
  onSelect: (selection: Selection) => void;
  /** Opens the Request-Client-Access modal. Omitted in the operator's read-only EA view, where the
   *  button is hidden entirely (an operator may only view the EA's clients). Also omitted on mobile,
   *  where the request affordance is a sticky bottom button owned by the parent. */
  onRequestAccess?: () => void;
  /**
   * Controls how the "My Clients" list renders (the Request/Search/Pending block stays on top at
   * every breakpoint):
   *  - "desktop" (default): narrow side-rail vertical list; the selected row is highlighted inline.
   *  - "tablet" : full-width 2-column grid of cards (detail opens in a Modal — no inline selection).
   *  - "mobile" : full-width single-column list of cards (detail opens in a Drawer).
   */
  layout?: "desktop" | "tablet" | "mobile";
}

/** Card used for the tablet grid / mobile list — bordered white tile with avatar + name. */
const ClientCard = ({ link, onClick }: { link: AccessLink; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-3 rounded-lg border border-neutral-gray bg-white p-4 text-left transition-colors hover:border-primary hover:bg-neutral-surface-gray"
  >
    <ClientAvatar name={link.party.name} avatar={link.party.avatar} size="md" />
    <Typography variant="action" className="min-w-0 flex-1 truncate font-semibold text-neutral-black">
      {link.party.name}
    </Typography>
  </button>
);

const PendingRow = ({ link, active, onClick }: { link: AccessLink; active: boolean; onClick: () => void }) => {
  const label = link.inviteState === "INVITED" ? "Invited to join" : "Awaiting approval";
  const time = formatRelativeShort(link.createdAt);
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "flex w-full items-center gap-3 p-3 text-left transition-colors",
        active ? "bg-primary-light" : "hover:bg-neutral-surface-gray"
      )}
    >
      <span
        className={clsx(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-dashed",
          active ? "border-primary" : "border-neutral-mid-gray"
        )}
      >
        <Icon name="check" isCustom size="sm" className={clsx(active ? "text-primary" : "text-neutral-silver-gray")} />
      </span>
      <span className="min-w-0 flex-1">
        <Typography variant="action" className={clsx("block truncate font-semibold", active ? "text-primary" : "text-neutral-black")}>
          {link.party.email || link.party.name}
        </Typography>
        <Typography variant="paragraph" className={clsx("block truncate", active ? "text-primary/80" : "text-neutral-silver-gray")}>
          {label}
          {time && <span> • {time}</span>}
        </Typography>
      </span>
    </button>
  );
};

/** EA "My Clients" left panel — request button, search, collapsible pending list, connected clients. */
export const ClientAccessList = ({
  clients,
  pending,
  isLoading,
  search,
  onSearch,
  selected,
  onSelect,
  onRequestAccess,
  layout = "desktop",
}: ClientAccessListProps) => {
  const [pendingOpen, setPendingOpen] = useState(true);
  const isCards = layout !== "desktop";

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 p-5">
      <div className="flex shrink-0 flex-col gap-2">
        {onRequestAccess && (
          <button
            type="button"
            onClick={onRequestAccess}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-neutral-silver-gray bg-neutral-gray p-3 text-neutral-dark-gray transition-colors hover:bg-neutral-mid-gray"
          >
            <Icon name="Add" size="md" />
            <Typography variant="action" className="font-semibold">
              Request Client Access
            </Typography>
          </button>
        )}

        <SearchInput className="w-full [&_input]:!min-w-0" placeholder="Search client" search={search} onSearch={onSearch} />

        {pending.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-warning bg-warning-light">
            <button type="button" onClick={() => setPendingOpen((o) => !o)} className="flex w-full items-center justify-between gap-2 p-3">
              <span className="flex items-center gap-2">
                <Icon name="Timer1" size="md" className="text-warning-dark" />
                <Typography variant="action" className="font-semibold text-warning-dark">
                  {pending.length} pending request{pending.length === 1 ? "" : "s"}
                </Typography>
              </span>
              <Icon name={pendingOpen ? "ArrowUp2" : "ArrowDown2"} size="md" className="text-warning-dark" />
            </button>
            {pendingOpen && (
              // Cap the height + scroll internally so a large pending count can't balloon this
              // shrink-0 header and squeeze the clients list below it (~4 rows visible, rest scroll).
              <div className="max-h-60 divide-y divide-neutral-gray overflow-y-auto border-t border-warning bg-white">
                {pending.map((link) => (
                  <PendingRow
                    key={link.id}
                    link={link}
                    active={selected?.kind === "pending" && selected.id === link.id}
                    onClick={() => onSelect({ kind: "pending", id: link.id })}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <Typography
          variant="small"
          className="mb-3 flex shrink-0 items-center gap-1.5 px-1 uppercase tracking-wide text-neutral-silver-gray"
        >
          My Clients
          <span className="inline-block h-1 w-1 rounded-full bg-neutral-silver-gray" />
          {clients.length}
        </Typography>

        {isLoading ? (
          <Loading />
        ) : clients.length === 0 ? (
          // Only the no-search-match hint here; the true-empty state is the graphic in the detail
          // panel ("No Clients to Manage"), so we don't repeat a "no clients yet" line in the list.
          search ? (
            <Typography variant="paragraph" className="px-1 py-4 text-neutral-dark-gray">
              No clients match your search.
            </Typography>
          ) : null
        ) : isCards ? (
          // Tablet 2-col grid / mobile 1-col list of cards. Detail opens in an overlay, so there is
          // no persistent inline selection to highlight here.
          <div
            className={clsx(
              // auto-rows-min + content-start keep the cards their natural height and packed at the
              // top — without them the single grid row stretches to fill flex-1 (giant cards).
              "grid min-h-0 flex-1 auto-rows-min content-start gap-3 overflow-y-auto",
              layout === "tablet" ? "grid-cols-2" : "grid-cols-1"
            )}
          >
            {clients.map((link) => (
              <ClientCard key={link.id} link={link} onClick={() => onSelect({ kind: "client", id: link.id })} />
            ))}
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
            {clients.map((link) => {
              const active = selected?.kind === "client" && selected.id === link.id;
              return (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => onSelect({ kind: "client", id: link.id })}
                  className={clsx(
                    "flex items-center gap-3 rounded-lg p-2.5 text-left transition-colors",
                    active ? "bg-primary-light" : "hover:bg-neutral-surface-gray"
                  )}
                >
                  <ClientAvatar name={link.party.name} avatar={link.party.avatar} size="sm" />
                  <Typography
                    variant="action"
                    className={clsx("min-w-0 flex-1 truncate font-semibold", active ? "text-primary" : "text-neutral-black")}
                  >
                    {link.party.name}
                  </Typography>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
